import { useCallback, useEffect, useState } from 'react';
import './App.scss';
import LoginModal from './modals/Login';
import RegistrationModal from './modals/Registration';
import ForgotPasswordModal from './modals/ForgotPassword';
import PricingModal from './modals/Pricing';
import CommitmentAccessModal from './modals/CommitmentAccess';
import ModalService from './services/Modal.service';
import { useTheme } from '@tuva-ui/components/src/Tuva-v2/utils';

// Default theme for common-modals (can be overridden by caller)
const DEFAULT_THEME = 'green';
const PARTNER_THEME = 'pink';
const JR_THEME = 'red';

// Tuva Jr. pages load tuva-styles-red.css (base.html gates it on `is_tuva_jr` /
// `/tuvajr/`), which is the reliable cross-page signal that the page is in the
// Jr red theme — `is_tuva_jr` isn't otherwise exposed to JS. Detect it so the
// login/registration modals match the Jr red instead of a mismatched pink/green.
const isTuvaJrPage = () =>
  typeof document !== 'undefined' &&
  (!!document.querySelector('link[href*="tuva-styles-red"]') ||
    /^\/tuvajr(-practice)?\//.test(window.location.pathname));

// Maps legacy revealModal names to React modal names
const MODAL_NAME_MAP = {
  logIn: 'login',
  logIn2: 'login',
  registration: 'registration',
  registrationModal: 'registration',
  teacherRegistration: 'teacherRegistration',
  studentRegistration: 'studentRegistration',
  'forgot-password': 'forgot-password',
  'request-quote': 'pricing',
  'request-demo': 'pricing',
  'contact-us': 'contact-us',
  'commitment-access': 'commitment-access',
};

function App() {
  const [modalName, setModalName] = useState();
  const [modalArgs, setModalArgs] = useState();
  const [subscription, setSubscription] = useState('premium');
  // Partner app is identified by app_config appname === 'partner' (Util → isPartner),
  // NOT the shared SUBDOMAIN (set for any subdomain). Partner → pink; else → green.
  const [isPartner, setIsPartner] = useState(false);
  // Jr. pages take priority — their red stylesheet is already loaded, so the
  // page is visibly red; match the modal to it (over partner pink / default).
  const [isTuvaJr] = useState(isTuvaJrPage);
  // Partner social-auth toggles from app_config
  const [socialAuth, setSocialAuth] = useState({});
  useTheme(isTuvaJr ? JR_THEME : isPartner ? PARTNER_THEME : DEFAULT_THEME);

  useEffect(() => {
    ModalService.getAppConfig().then(cfg => {
      if (cfg && cfg.isPartner) setIsPartner(true);
      if (cfg) {
        setSocialAuth({
          google: cfg.socialAuthGoogleEnabled,
          microsoft: cfg.socialAuthMicrosoftEnabled,
        });
      }
    });
  }, []);

  const onCancel = useCallback(() => {
    setModalName();
    setModalArgs();
  }, []);

  const onSwitchModal = useCallback((name, args) => {
    const mapped = MODAL_NAME_MAP[name] || name;
    setModalName(mapped);
    setModalArgs(args);
  }, []);

  useEffect(() => {
    // New React API
    window.openCommonModal = (modelName, args) => {
      setModalName(modelName);
      if (typeof args === 'string') {
        setSubscription(args);
      } else {
        setModalArgs(args);
      }
    };

    // Backward-compatible bridge — overrides AngularJS version
    window.revealModal = function (modalName) {
      const args = arguments.length > 1 ? arguments[1] : undefined;
      const reactName = MODAL_NAME_MAP[modalName];

      if (reactName) {
        // Handle special cases
        if (modalName === 'teacherRegistration') {
          setModalName('registration');
          setModalArgs({ userType: 'teacher' });
        } else if (modalName === 'studentRegistration') {
          setModalName('registration');
          setModalArgs(
            args ? { userType: 'student', ...args } : { userType: 'student' },
          );
        } else if (modalName === 'logIn' || modalName === 'logIn2') {
          setModalName('login');
          setModalArgs(args);
        } else {
          setModalName(reactName);
          setModalArgs(args);
        }
      }
      // Unsupported modals (contact-us, wopi, etc.) fall through silently
      // They will continue to be handled by app.commonmodals.js until migrated
    };
  }, []);

  let modal;
  switch (modalName) {
    case 'pricing':
      modal = <PricingModal onCancel={onCancel} subscription={subscription} />;
      break;
    case 'commitment-access':
      modal = <CommitmentAccessModal onCancel={onCancel} />;
      break;
    case 'login':
      modal = (
        <LoginModal
          onCancel={onCancel}
          onSwitchModal={onSwitchModal}
          args={modalArgs}
          isPartner={isPartner}
          socialAuth={socialAuth}
        />
      );
      break;
    case 'registration':
      modal = (
        <RegistrationModal
          onCancel={onCancel}
          onSwitchModal={onSwitchModal}
          args={modalArgs}
          isPartner={isPartner}
          socialAuth={socialAuth}
        />
      );
      break;
    case 'forgot-password':
      modal = (
        <ForgotPasswordModal
          onCancel={onCancel}
          onSwitchModal={onSwitchModal}
        />
      );
      break;
    default:
      return <></>;
  }

  return modal;
}

export default App;
